import { useState, useRef, useEffect, useCallback } from "react";

// ─── Utilities ─────────────────────────────────────────────────────────────
function hexToHsv(hex: string): [number, number, number] {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const delta = max - min;
    let h = 0;
    if (delta !== 0) {
        if (max === r) h = ((g - b) / delta) % 6;
        else if (max === g) h = (b - r) / delta + 2;
        else h = (r - g) / delta + 4;
        h = Math.round(h * 60);
        if (h < 0) h += 360;
    }
    const s = max === 0 ? 0 : delta / max;
    const v = max;
    return [h, s, v];
}

function hsvToHex(h: number, s: number, v: number): string {
    const f = (n: number) => {
        const k = (n + h / 60) % 6;
        return v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
    };
    const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, "0");
    return `#${toHex(f(5))}${toHex(f(3))}${toHex(f(1))}`;
}

function isValidHex(hex: string) {
    return /^#[0-9a-fA-F]{6}$/.test(hex);
}

// ─── ColorPickerInput ──────────────────────────────────────────────────────
function ColorPickerInput({
    label,
    value,
    onChange,
    disabled = false,
}: {
    label: string;
    value: string;
    onChange: (hex: string) => void;
    disabled?: boolean;
}) {
    const safeHex = isValidHex(value) ? value : "#ffffff";
    const [hsv, setHsv] = useState<[number, number, number]>(() => hexToHsv(safeHex));
    const [hexInput, setHexInput] = useState(safeHex.toUpperCase());
    const [open, setOpen] = useState(false);

    const popoverRef = useRef<HTMLDivElement>(null);
    const gradientRef = useRef<HTMLDivElement>(null);
    const hueRef = useRef<HTMLDivElement>(null);

    // Sync external value → internal state
    useEffect(() => {
        if (isValidHex(value) && value.toUpperCase() !== hexInput) {
            setHsv(hexToHsv(value));
            setHexInput(value.toUpperCase());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Close picker on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const commit = useCallback(
        (h: number, s: number, v: number) => {
            const hex = hsvToHex(h, s, v);
            setHexInput(hex.toUpperCase());
            onChange(hex);
        },
        [onChange]
    );

    // Gradient (Saturation / Value) drag
    const handleGradientPointer = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            if (!gradientRef.current) return;
            const rect = gradientRef.current.getBoundingClientRect();
            const s = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
            const v = Math.min(1, Math.max(0, 1 - (e.clientY - rect.top) / rect.height));
            const next: [number, number, number] = [hsv[0], s, v];
            setHsv(next);
            commit(...next);
        },
        [hsv, commit]
    );

    const startGradientDrag = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            handleGradientPointer(e);
        },
        [handleGradientPointer]
    );

    // Hue bar drag
    const handleHuePointer = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            if (!hueRef.current) return;
            const rect = hueRef.current.getBoundingClientRect();
            const h = Math.min(360, Math.max(0, ((e.clientX - rect.left) / rect.width) * 360));
            const next: [number, number, number] = [h, hsv[1], hsv[2]];
            setHsv(next);
            commit(...next);
        },
        [hsv, commit]
    );

    const startHueDrag = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            handleHuePointer(e);
        },
        [handleHuePointer]
    );

    // Hex text input
    const handleHexInput = (raw: string) => {
        const normalized = raw.startsWith("#") ? raw : `#${raw}`;
        setHexInput(normalized.toUpperCase());
        if (isValidHex(normalized)) {
            setHsv(hexToHsv(normalized));
            onChange(normalized);
        }
    };

    const currentHex = hsvToHex(...hsv);
    const hueOnlyHex = hsvToHex(hsv[0], 1, 1);
    const thumbX = hsv[1] * 100;
    const thumbY = (1 - hsv[2]) * 100;
    const hueX = (hsv[0] / 360) * 100;

    const QUICK_COLORS = [
        "#f97316", "#3b82f6", "#10b981", "#a855f7", "#ef4444",
        "#06b6d4", "#eab308", "#ec4899", "#E7664C", "#26A69A",
        "#ffffff", "#6b7280", "#000000",
    ];

    return (
        <div className="flex flex-col gap-1.5 relative" ref={popoverRef}>
            {/* Label */}
            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">
                {label}
            </label>

            {/* Trigger button */}
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((o) => !o)}
                className={[
                    "w-full flex items-center gap-2.5 bg-gray-950 border rounded-lg px-3 py-2.5",
                    "text-xs font-semibold transition-colors text-left focus:outline-none",
                    disabled
                        ? "opacity-40 cursor-not-allowed border-gray-700 text-gray-500"
                        : "border-gray-700 hover:border-orange-500/50 text-white cursor-pointer",
                ].join(" ")}
            >
                <span
                    className="w-5 h-5 rounded-md border border-white/10 shrink-0"
                    style={{ backgroundColor: currentHex }}
                />
                <span className="font-mono tracking-wider">{hexInput}</span>
            </button>

            {/* Color Picker Popover */}
            {open && (
                <div
                    className="absolute top-full left-0 mt-2 z-50 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden"
                    style={{ width: 272 }}
                >
                    {/* ── Saturation / Value gradient canvas ── */}
                    <div
                        ref={gradientRef}
                        className="relative w-full cursor-crosshair select-none"
                        style={{
                            height: 180,
                            background: `linear-gradient(to top, #000, transparent),
                                         linear-gradient(to right, #fff, ${hueOnlyHex})`,
                        }}
                        onPointerDown={startGradientDrag}
                        onPointerMove={(e) => { if (e.buttons === 1) handleGradientPointer(e); }}
                    >
                        {/* Crosshair thumb */}
                        <div
                            className="absolute w-4 h-4 rounded-full border-2 border-white -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                            style={{
                                left: `${thumbX}%`,
                                top: `${thumbY}%`,
                                backgroundColor: currentHex,
                                boxShadow: "0 0 0 1px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.5)",
                            }}
                        />
                    </div>

                    <div className="p-3 flex flex-col gap-3">
                        {/* ── Hue rainbow bar ── */}
                        <div
                            ref={hueRef}
                            className="relative h-3 rounded-full cursor-pointer select-none"
                            style={{
                                background:
                                    "linear-gradient(to right,#f00 0%,#ff0 17%,#0f0 33%,#0ff 50%,#00f 67%,#f0f 83%,#f00 100%)",
                            }}
                            onPointerDown={startHueDrag}
                            onPointerMove={(e) => { if (e.buttons === 1) handleHuePointer(e); }}
                        >
                            <div
                                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-white pointer-events-none"
                                style={{
                                    left: `${hueX}%`,
                                    backgroundColor: hueOnlyHex,
                                    boxShadow: "0 0 0 1px rgba(0,0,0,0.4)",
                                }}
                            />
                        </div>

                        {/* ── Hex input row ── */}
                        <div className="flex items-center gap-2">
                            <div
                                className="w-8 h-8 rounded-md border border-white/10 shrink-0"
                                style={{ backgroundColor: currentHex }}
                            />
                            <div className="flex-1 relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 font-mono select-none">
                                    HEX
                                </span>
                                <input
                                    type="text"
                                    value={hexInput}
                                    onChange={(e) => handleHexInput(e.target.value)}
                                    maxLength={7}
                                    className="w-full bg-gray-950 border border-gray-700 rounded-lg pl-10 pr-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-orange-500 transition-colors uppercase"
                                    placeholder="#FFFFFF"
                                />
                            </div>
                        </div>

                        {/* ── Quick-pick swatches ── */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                            {QUICK_COLORS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    title={c}
                                    onClick={() => {
                                        setHsv(hexToHsv(c));
                                        setHexInput(c.toUpperCase());
                                        onChange(c);
                                    }}
                                    className="w-5 h-5 rounded-md border border-white/10 hover:scale-110 transition-transform shrink-0 focus:outline-none  "
                                    style={{ backgroundColor: c }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ColorPickerInput;